/**
 * resolveRole — TV-RV-01
 *
 * Single source of truth for role resolution in the TeleVisit app.
 * Consumed by all auth paths (EHR launch via SmartCallback, EHR resume via
 * AuthProvider.initEHRModeWithClient, and standalone KC mode).
 *
 * SECURITY MODEL
 * ──────────────
 * KC roles (from the decoded access token) are authoritative. The `launchHint`
 * (?role=) is a tiebreaker only among roles the user already holds — it cannot
 * grant a view the token doesn't back. Specifically:
 *   - A patient with ?role=provider still resolves to patient (no clinical KC
 *     role in their token → FRONT_DESK and CLINICAL checks both miss → no KC
 *     patient role either → fhirUser fallback → Patient/ prefix → patient).
 *   - A user with no KC roles falls back to the fhirUser prefix (legacy EHR
 *     launches where KC roles are not in the access token yet — see TV-RV-03).
 *
 * DUAL-ROLE DEFAULT (decided 2026-06-20)
 * ───────────────────────────────────────
 * A user holding both front-desk and clinical roles (e.g. a Practice Manager
 * who is also a clinician) gets:
 *   - front-desk when launched from karepractice (?role=front-desk)
 *   - provider  when launched from kareMD        (?role=provider)
 * The hint is only consulted among roles the user actually holds; role-match
 * ordering (front-desk first) is the no-hint fallback.
 */

import karemdRuleset from '@collabkare/ability/rules/karemd.rules.json';

// ── Public constants ─────────────────────────────────────────────────────────

export const ROLES = {
  FRONT_DESK: 'front-desk',
  PROVIDER:   'provider',
  PATIENT:    'patient',
  ADMIN:      'admin',
};

// ── Private helpers ──────────────────────────────────────────────────────────

/**
 * Canonicalize a KC role name for set membership tests.
 * "Front Desk Staff" → "front_desk_staff"
 * "nurse-practitioner" → "nurse_practitioner"
 */
export function canonicalize(r) {
  return String(r).toLowerCase().replace(/[\s-]+/g, '_');
}

// Clinical roles from the karemd matrix — any role in this set → provider
// (full chart/careplan access). The JSON uses kebab-case; we canonicalize.
//
// TV-RV-QC fix (2026-06-20): the karemd ruleset's `roles` array also contains
// NON-clinical practice/IT personas (program-administrator, system-support).
// Those must NOT resolve to `provider` — that would grant PHI/chart access to
// a non-clinician. Exclude them explicitly so CLINICAL = clinicians only.
// If KarePractice/karemd add more non-clinical roles to the matrix, extend
// this exclusion set (or switch to an explicit clinician allowlist).
const NON_CLINICAL_KAREMD_ROLES = new Set([
  'program_administrator',
  'system_support',
]);
const CLINICAL = new Set(
  karemdRuleset.roles
    .map(canonicalize)
    .filter((r) => !NON_CLINICAL_KAREMD_ROLES.has(r)),
);

// Front-desk role set — from the KarePractice RBAC matrix
// (Practice Manager, Front Desk Staff, Patient Access Rep, Billing Specialist,
// Credentialing Coordinator, IT Support, Revenue Cycle Staff, Program Admin).
// Only the three "front-desk view" roles from §3.1 of the design doc drive
// the front-desk view. The full karepractice matrix is consulted in Slice 2.
const FRONT_DESK = new Set([
  'front_desk_staff',
  'patient_access_rep',
  'practice_manager',
]);

// ── Main export ──────────────────────────────────────────────────────────────

/**
 * Resolves the effective TeleVisit role for a user.
 *
 * @param {object} opts
 * @param {string}  opts.fhirUser   - e.g. "Practitioner/dr-001" (may be empty)
 * @param {object}  opts.kcClaims   - decoded ACCESS token payload
 *                                    (has realm_access.roles + resource_access)
 * @param {string}  opts.launchHint - value of ?role= query param (may be empty/null)
 * @returns {'front-desk'|'provider'|'patient'|'admin'}
 */
export function resolveRole({ fhirUser = '', kcClaims = {}, launchHint = '' }) {
  // Flatten realm + all client roles into one canonical list.
  const roles = [
    ...(kcClaims?.realm_access?.roles || []),
    ...Object.values(kcClaims?.resource_access || {}).flatMap((r) => r.roles || []),
  ].map(canonicalize);

  // ── 1. KC roles are authoritative ─────────────────────────────────────────
  // Check order: front-desk first (so a dual-role person without a hint gets
  // front-desk as the conservative default; kareMD overrides via ?role=provider).
  if (roles.some((r) => FRONT_DESK.has(r))) {
    return narrow(ROLES.FRONT_DESK, launchHint, roles);
  }
  if (roles.some((r) => CLINICAL.has(r))) {
    return narrow(ROLES.PROVIDER, launchHint, roles);
  }
  if (roles.includes('admin')) return ROLES.ADMIN;
  if (roles.includes('patient')) return ROLES.PATIENT;

  // ── 2. fhirUser prefix fallback ───────────────────────────────────────────
  // Only reaches here when no KC roles are in the token (pre-TV-RV-03 legacy
  // EHR launches). Cannot distinguish front-desk from provider here — both
  // are Practitioner/… — so default to provider (matches prior behaviour).
  if (fhirUser.startsWith('Practitioner/')) return ROLES.PROVIDER;
  if (fhirUser.startsWith('Patient/') || fhirUser.startsWith('RelatedPerson/')) {
    return ROLES.PATIENT;
  }

  // ── 3. Unknown / anonymous ────────────────────────────────────────────────
  return ROLES.PATIENT;
}

/**
 * Apply the launcher's hint ONLY if the user actually holds the hinted role.
 *
 * This is the spoof-resistance gate: narrow() is always called with the
 * role-match result as `resolved` — a patient who reaches narrow() will never
 * do so, because patient hits the `roles.includes('patient')` branch before
 * narrow() can fire. But even in theory: if a patient somehow called narrow()
 * with hint='provider', roles.some(r => CLINICAL.has(r)) would be false, so
 * the hint branch is skipped and `resolved` is returned unchanged.
 *
 * @param {string}   resolved - the role determined by KC role-match ordering
 * @param {string}   hint     - raw ?role= value from the launcher
 * @param {string[]} roles    - canonical roles from the token
 * @returns {string}
 */
function narrow(resolved, hint, roles) {
  const h = canonicalize(String(hint || '').replace(/-/g, '_'));
  if (h === 'front_desk' && roles.some((r) => FRONT_DESK.has(r))) return ROLES.FRONT_DESK;
  if (h === 'provider'   && roles.some((r) => CLINICAL.has(r)))   return ROLES.PROVIDER;
  return resolved;
}
